import { Config, JOGOS_360, PLAY_GAMES_CLID } from '@/composables/use-clid-helpers'
import { AvailableLocale } from '@/i18n/helpers'
import type { TR } from '@/i18n/i18n-global'
import { AdLoader } from '@/modules/adv/ad-loader'
import { OutOfPageAdType } from '@/modules/adv/types'
import type { APIGameType, CategoryShortInfo, GameCategoriesInfoType } from '@/types'

export function getSubCategories(
    type: 'category' | 'tag' | 'series',
    gameCatInfo: GameCategoriesInfoType,
): CategoryShortInfo[] {
    return gameCatInfo.categoriesInfo.filter((cat) => cat.categoryType === type)
}

export function getMainCategory(
    game: APIGameType | undefined,
    gameCatInfo: GameCategoriesInfoType | undefined,
): CategoryShortInfo | undefined {
    return gameCatInfo?.categoriesInfo.find((cat) => cat.id === game?.main_category)
}

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Permissions_Policy#browser_compatibility
// this is used in both export and game frame in portal
export const iframeAllowAttribute = [
    // could be used in gameplay
    'fullscreen',
    'accelerometer',
    'camera',
    'clipboard-read',
    'clipboard-write',
    'screen-wake-lock',
    'speaker-selection',
    'web-share',
    'geolocation',
    'gyroscope',
    'microphone',
    'xr-spatial-tracking',

    // the props below are just in case
    // for now no ad/analytics/purchases are happen on game frame level

    // could be used in ad
    'autoplay',
    'encrypted-media',
    'picture-in-picture',
    // could be used in in-game purchases
    'payment',
    'publickey-credentials-get',
    'publickey-credentials-create',
    // for analytics
    'storage-access',
    'attribution-reporting',
    'browsing-topics',
].join(';')

function getDeveloperInfo(game: APIGameType | undefined, tr: TR): string {
    return game?.developer && game?.developer !== 'Playgama'
        ? tr('headMetaGame.developer', { developer: game?.developer })
        : ''
}

function getUpdateDateInfo(game: APIGameType | undefined, tr: TR, currentLocale: AvailableLocale): string {
    return game?.updatedAt
        ? tr('headMetaGame.updateDate', {
              date: new Date(game.updatedAt).toLocaleDateString(currentLocale, { month: 'short', year: 'numeric' }),
          })
        : ''
}

export function getGameDescription(game: APIGameType | undefined, tr: TR) {
    return (
        game?.meta_description?.trim() ||
        `${tr('headMetaGame.description', { title: game?.title ?? '' })} ${getDeveloperInfo(game, tr)}`.trim()
    )
}

export function getGameTitleWithDeveloperInfo(game: APIGameType | undefined, tr: TR, isBold: boolean): string {
    const metaTitle = game?.meta_title?.trim()

    if (metaTitle) {
        return metaTitle
    }

    return isBold
        ? `${tr('headMetaGame.titleBold', { title: game?.title ?? '' })} ${getDeveloperInfo(game, tr)}`.trim()
        : `${tr('headMetaGame.title', { title: game?.title ?? '' })} ${getDeveloperInfo(game, tr)}`.trim()
}

export function getGameTitleWithUpdateInfo(
    game: APIGameType | undefined,
    tr: TR,
    isBold: boolean,
    currentLocale: AvailableLocale,
): string {
    const metaTitle = game?.meta_title?.trim()

    if (metaTitle) {
        return metaTitle
    }

    return isBold
        ? `${tr('headMetaGame.titleBold', { title: game?.title ?? '' })} ${getUpdateDateInfo(game, tr, currentLocale)}`.trim()
        : `${tr('headMetaGame.title', { title: game?.title ?? '' })} ${getUpdateDateInfo(game, tr, currentLocale)}`.trim()
}

const progressColorByClid: Record<string, string> = {
    [JOGOS_360]: '#1168e8',
    [PLAY_GAMES_CLID]: '#38b646',
}

export function getAdLoader(options: {
    affiliate?: boolean
    adConfig?: Config
    clid: string
    progressColor?: string
    getTimeout: (adType: OutOfPageAdType) => number
    subtitlesByAdType?: Partial<Record<OutOfPageAdType, string>>
}) {
    if (options.adConfig?.isPreloaderDisabled) {
        return undefined
    }

    return new AdLoader({
        ...options,
        progressColor: progressColorByClid[options.clid],
        logoId: options.adConfig?.isCustomized ? options.clid : '',
    })
}

export const LOADER_FILLER_DURATION_MS = 3000
